import { useCallback, useEffect, useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import t from "@/lang/lang";
import {
  useQpmOfferConfigAddSectionMutation,
  useQpmOfferConfigCreateTextBlockMutation,
  useQpmOfferConfigGetSettingsQuery,
  useQpmOfferConfigListSectionsQuery,
  useQpmOfferConfigListTextBlocksQuery,
  useQpmOfferConfigPutSettingsMutation,
  useQpmOfferConfigReorderTextBlocksMutation,
  useQpmOfferConfigUpdateTextBlockAlwaysDisplayMutation,
  useQpmOfferConfigUpdateTextBlockBodyMutation,
  useQpmOfferConfigUpdateTextBlockTitleMutation,
} from "@/services/backend/qpm/offer-config/api/offer-config-api";
import {
  QpmCeoGender,
  TextBlockResponse,
} from "@/services/backend/qpm/offer-config/types/offer-config";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Editor,
  EditorBoldItalicUnderlineGroup,
  EditorBulletList,
  EditorContent,
  editorGenerateHTML,
  EditorOrderedList,
  EditorToolbar,
  EditorToolbarSeparator,
  useEditor,
} from "@/shared/components/editor/editor";
import { Check, GripVertical, Pencil, Plus, X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  OfferConfigTextBlockSection,
  OfferTextBlockSectionUtil,
} from "@/services/backend/qpm/offer-config/types/offer-config-enum";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { usePersistenceStateUpdateOnTrigger } from "@/shared/lib/persistence-state/persistence-state-info";

export function OfferDataRoutePage() {
  const { data, error, isLoading } = useQpmOfferConfigGetSettingsQuery({});
  const [
    putSettings,
    {
      isLoading: putSettingsIsLoading,
      error: putSettingsError,
      isSuccess: putSettingsIsSuccess,
      reset: putSettingsReset,
    },
  ] = useQpmOfferConfigPutSettingsMutation();

  const { data: sectionsData } = useQpmOfferConfigListSectionsQuery({});
  const [addSection, addSectionState] = useQpmOfferConfigAddSectionMutation();

  const [ceoName, setCeoName] = useState<string>("");
  const [ceoGender, setCeoGender] = useState<QpmCeoGender>("");
  const [offerClaimText, setOfferClaimText] = useState<string>("");
  const [isEditingOfferClaim, setIsEditingOfferClaim] = useState(false);
  const [newSectionName, setNewSectionName] = useState<string>("");

  const initialCeoName = useMemo(() => data?.ceoName ?? "", [data?.ceoName]);
  const initialCeoGender = useMemo(
    () => data?.ceoGender ?? "",
    [data?.ceoGender],
  );
  const initialOfferClaimText = useMemo(
    () => data?.offerClaimText ?? "",
    [data?.offerClaimText],
  );

  useEffect(() => {
    setCeoName(initialCeoName);
  }, [initialCeoName]);

  useEffect(() => {
    setCeoGender(initialCeoGender);
  }, [initialCeoGender]);

  useEffect(() => {
    setOfferClaimText(initialOfferClaimText);
    setIsEditingOfferClaim(false);
  }, [initialOfferClaimText]);

  const settingsRequest = useMemo(
    () => ({
      id: data?.id,
      ceoName,
      ceoGender,
      allowedDiscounts: data?.allowedDiscounts ?? [],
      allowedSurcharges: data?.allowedSurcharges ?? [],
      offerClaimText,
      serviceProgramGroups: data?.serviceProgramGroups ?? [],
      serviceProgramEntries: data?.serviceProgramEntries ?? [],
    }),
    [
      ceoName,
      ceoGender,
      data?.allowedDiscounts,
      data?.allowedSurcharges,
      data?.id,
      data?.serviceProgramGroups,
      data?.serviceProgramEntries,
      offerClaimText,
    ],
  );

  const { onTrigger: triggerSaveSettings } = usePersistenceStateUpdateOnTrigger(
    settingsRequest,
    putSettings,
    putSettingsIsLoading,
    putSettingsError,
    putSettingsIsSuccess,
    putSettingsReset,
    { toastError: true, toastSuccess: true },
  );

  useEffect(() => {
    if (putSettingsIsSuccess) {
      putSettingsReset();
      setIsEditingOfferClaim(false);
    }
  }, [putSettingsIsSuccess, putSettingsReset]);

  const [orderedSections, setOrderedSections] = useState<
    OfferConfigTextBlockSection[]
  >([]);

  useEffect(() => {
    // Keep stable order once user reorders locally.
    if (!sectionsData?.items) return;

    setOrderedSections((prev) => {
      if (prev.length === 0) return sectionsData.items;

      const next = prev.filter((s) => sectionsData.items.includes(s));
      sectionsData.items
        .filter((s) => !next.includes(s))
        .forEach((s) => next.push(s));
      return next;
    });
  }, [sectionsData?.items]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleSectionDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedSections((prev) => {
      const oldIndex = prev.findIndex((s) => s === active.id);
      const newIndex = prev.findIndex((s) => s === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const availableSections = useMemo(() => {
    const used = new Set((sectionsData?.items ?? []).map((s) => String(s)));
    const all = Object.values(OfferConfigTextBlockSection);
    return all.filter((s) => !used.has(s));
  }, [sectionsData?.items]);

  const { toast } = useToast();

  const handleAddSection = useCallback(async () => {
    if (!newSectionName) {
      return;
    }

    try {
      await addSection({ section: newSectionName }).unwrap();
      setNewSectionName("");
      toast({
        title: t("Speichern erfolgreich!"),
        description: t("Die Daten wurden vom System übernommen."),
        variant: "success",
      });
    } catch (mutationError) {
      const parsed = parseRTKQueryError(mutationError as never, false);
      toast({
        title: parsed?.title ?? t("Fehler beim Speichern"),
        description: parsed?.description ?? "",
        variant: "destructive",
      });
    }
  }, [addSection, newSectionName, toast]);

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (isLoading) return <Skeleton className="h-20 w-full" />;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("Angebotsdaten")}</CardTitle>
          <CardDescription>
            {t(
              "Vorbereitung: Diese Seite verwendet Offer-Config. Die finale Darstellung wird später speziell umgesetzt.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">{t("Geschäftsführung")}</div>
            <Input
              value={ceoName}
              placeholder={t("Name")}
              onChange={(e) => setCeoName(e.target.value)}
            />
            <Select
              value={ceoGender}
              onValueChange={(value) => setCeoGender(value as QpmCeoGender)}
            >
              <SelectTrigger aria-label={t("Bezeichnung")}>
                <SelectValue placeholder="m / w / d" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="m">m</SelectItem>
                <SelectItem value="w">w</SelectItem>
                <SelectItem value="d">d</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-start">
            <Button
              onClick={triggerSaveSettings}
              disabled={putSettingsIsLoading}
            >
              {t("Speichern")}
            </Button>
          </div>

          {putSettingsError && <RTKQueryErrorAlert error={putSettingsError} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("Angebotsdisclaimer")}</CardTitle>
          <CardDescription>
            {t("Disclaimer für Angebote bearbeiten.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">{t("Angebotsdisclaimer")}</div>
            <TooltipProvider>
              {isEditingOfferClaim ? (
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={triggerSaveSettings}
                        disabled={putSettingsIsLoading}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("Speichern")}</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => {
                          setOfferClaimText(initialOfferClaimText);
                          setIsEditingOfferClaim(false);
                        }}
                        disabled={putSettingsIsLoading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("Abbrechen")}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => setIsEditingOfferClaim(true)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("Bearbeiten")}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>

          <TipTapHtmlEditor
            value={offerClaimText}
            onChange={setOfferClaimText}
            disabled={putSettingsIsLoading}
            editable={isEditingOfferClaim}
            showToolbar={isEditingOfferClaim}
          />

          {putSettingsError && <RTKQueryErrorAlert error={putSettingsError} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("Sektionen")}</CardTitle>
          <CardDescription>
            {t(
              "Sektionen werden als Titel angezeigt. Darunter befinden sich Textblöcke.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium">{t("Neue Sektion")}</div>
            <div className="flex gap-2">
              <Select
                value={newSectionName}
                onValueChange={(val) => setNewSectionName(val)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("Bitte wählen")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {availableSections.map((s) => (
                      <SelectItem key={s} value={s}>
                        {OfferTextBlockSectionUtil.toString(s)}
                      </SelectItem>
                    ))}
                    {availableSections.length === 0 && (
                      <SelectItem value="__none" disabled>
                        {t("Keine verfügbaren Sektionen")}
                      </SelectItem>
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>

              <Button
                disabled={!newSectionName || addSectionState.isLoading}
                onClick={handleAddSection}
              >
                <Plus className="h-4 w-4" />
                <span className="ml-2">{t("Hinzufügen")}</span>
              </Button>
            </div>
            {addSectionState.error && (
              <RTKQueryErrorAlert error={addSectionState.error} />
            )}
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleSectionDragEnd}
          >
            <SortableContext
              items={orderedSections}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-4">
                {orderedSections.map((section) => (
                  <SectionCard key={section} section={section} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>
    </div>
  );
}

function SectionCard({ section }: { section: OfferConfigTextBlockSection }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const [createTextBlockOpen, setCreateTextBlockOpen] = useState(false);

  return (
    <div ref={setNodeRef} style={style} className="rounded-md border">
      <div className="flex items-center justify-between gap-2 border-b p-3">
        <div className="flex min-w-0 items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            type="button"
            className="hover:bg-muted cursor-grab rounded p-1"
            title={t("Verschieben")}
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </button>
          <div className="truncate text-base font-semibold">
            {OfferTextBlockSectionUtil.toString(section)}
          </div>
        </div>

        <Button
          size="sm"
          variant="outline"
          disabled={createTextBlockOpen}
          onClick={() => setCreateTextBlockOpen(true)}
        >
          <Plus className="h-4 w-4" />
          <span className="ml-2">{t("Textblock")}</span>
        </Button>
      </div>
      <div className="p-3">
        <SectionTextBlocks
          section={section}
          createTextBlockOpen={createTextBlockOpen}
          onCreateTextBlockOpenChange={setCreateTextBlockOpen}
        />
      </div>
    </div>
  );
}

function SectionTextBlocks({
  section,
  createTextBlockOpen,
  onCreateTextBlockOpenChange,
}: {
  section: string;
  createTextBlockOpen: boolean;
  onCreateTextBlockOpenChange: (open: boolean) => void;
}) {
  const { data, error, isLoading } = useQpmOfferConfigListTextBlocksQuery({
    section,
  });

  const [createTextBlock, createState] =
    useQpmOfferConfigCreateTextBlockMutation();
  const [updateBody, updateBodyState] =
    useQpmOfferConfigUpdateTextBlockBodyMutation();
  const [updateTitle, updateTitleState] =
    useQpmOfferConfigUpdateTextBlockTitleMutation();
  const [reorder, reorderState] = useQpmOfferConfigReorderTextBlocksMutation();
  const [updateAlwaysDisplay, updateAlwaysDisplayState] =
    useQpmOfferConfigUpdateTextBlockAlwaysDisplayMutation();

  const [createMode, setCreateMode] = useState(false);
  const [newTitle, setNewTitle] = useState<string>("");
  const [newBody, setNewBody] = useState<string>("");
  const [newAlwaysDisplay, setNewAlwaysDisplay] = useState<boolean>(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [editBody, setEditBody] = useState<string>("");
  const [editAlwaysDisplay, setEditAlwaysDisplay] = useState<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const { toast } = useToast();

  const showMutationErrorToast = useCallback(
    (mutationError: unknown) => {
      const parsed = parseRTKQueryError(mutationError as never, false);
      toast({
        title: parsed?.title ?? t("Fehler beim Speichern"),
        description: parsed?.description ?? "",
        variant: "destructive",
      });
    },
    [toast],
  );

  const showMutationSuccessToast = useCallback(() => {
    toast({
      title: t("Speichern erfolgreich!"),
      description: t("Die Daten wurden vom System übernommen."),
      variant: "success",
    });
  }, [toast]);

  const handleTextBlockDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (reorderState.isLoading || !data) return;

      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const orderedIds = data.items.map((i) => i.id);
      const oldIndex = orderedIds.findIndex((id) => id === active.id);
      const newIndex = orderedIds.findIndex((id) => id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(orderedIds, oldIndex, newIndex);
      try {
        await reorder({ section, orderedIds: next }).unwrap();
      } catch (mutationError) {
        showMutationErrorToast(mutationError);
      }
    },
    [data, reorder, reorderState.isLoading, section, showMutationErrorToast],
  );

  const startEdit = (tb: TextBlockResponse) => {
    setEditingId(tb.id);
    setEditTitle(tb.title ?? "");
    setEditBody(tb.body ?? "");
    setEditAlwaysDisplay(tb.alwaysDisplay);
    setCreateMode(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditBody("");
    setEditAlwaysDisplay(false);
  };

  const cancelCreate = () => {
    setCreateMode(false);
    setNewTitle("");
    setNewBody("");
    setNewAlwaysDisplay(false);
  };

  useEffect(() => {
    if (createTextBlockOpen) {
      setCreateMode(true);
      setEditingId(null);
      onCreateTextBlockOpenChange(false);
    }
  }, [createTextBlockOpen, onCreateTextBlockOpenChange]);

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (isLoading) return <Skeleton className="h-10 w-full" />;

  const items = data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* moved create button to section header */}

      {createMode && (
        <div className="rounded-md border p-3">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-medium">{t("Neuer Textblock")}</div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  const order = items.length;
                  try {
                    await createTextBlock({
                      section,
                      order,
                      title: newTitle,
                      body: newBody,
                    }).unwrap();
                    cancelCreate();
                    showMutationSuccessToast();
                  } catch (mutationError) {
                    showMutationErrorToast(mutationError);
                  }
                }}
                disabled={createState.isLoading}
              >
                {t("Speichern")}
              </Button>
              <Button size="sm" variant="outline" onClick={cancelCreate}>
                {t("Abbrechen")}
              </Button>
            </div>
          </div>

          <div className="mb-3 flex flex-col gap-2">
            <div className="text-sm font-medium">{t("Titel")}</div>
            <Input
              value={newTitle}
              placeholder={t("Titel")}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          </div>

          <div className="mb-3 flex items-center gap-2">
            <Checkbox
              checked={newAlwaysDisplay}
              onCheckedChange={(v) => setNewAlwaysDisplay(!!v)}
            />
            <div className="text-sm">{t("Standardmäßig anzeigen")}</div>
          </div>

          <TipTapHtmlEditor
            value={newBody}
            onChange={setNewBody}
            disabled={createState.isLoading}
          />

          {createState.error && (
            <RTKQueryErrorAlert error={createState.error} />
          )}
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleTextBlockDragEnd}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-3">
            {items.map((tb) => (
              <TextBlockCard
                key={tb.id}
                textBlock={tb}
                isEditing={editingId === tb.id}
                editTitle={editTitle}
                editBody={editBody}
                editAlwaysDisplay={editAlwaysDisplay}
                onStartEdit={() => startEdit(tb)}
                onCancelEdit={cancelEdit}
                onChangeTitle={setEditTitle}
                onChangeBody={setEditBody}
                onChangeAlwaysDisplay={setEditAlwaysDisplay}
                onSave={async () => {
                  // Title and body are updated via separate backend endpoints.
                  try {
                    await Promise.all([
                      updateTitle({ id: tb.id, title: editTitle }).unwrap(),
                      updateBody({ id: tb.id, body: editBody }).unwrap(),
                    ]);
                    cancelEdit();
                    showMutationSuccessToast();
                  } catch (mutationError) {
                    showMutationErrorToast(mutationError);
                  }
                }}
                isSaving={
                  updateBodyState.isLoading || updateTitleState.isLoading
                }
                onToggleAlwaysDisplay={async (next) => {
                  try {
                    await updateAlwaysDisplay({
                      id: tb.id,
                      alwaysDisplay: next,
                    }).unwrap();
                    showMutationSuccessToast();
                  } catch (mutationError) {
                    showMutationErrorToast(mutationError);
                    throw mutationError;
                  }
                }}
                isAlwaysDisplayUpdating={updateAlwaysDisplayState.isLoading}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {reorderState.error && <RTKQueryErrorAlert error={reorderState.error} />}
      {updateTitleState.error && (
        <RTKQueryErrorAlert error={updateTitleState.error} />
      )}
      {updateBodyState.error && (
        <RTKQueryErrorAlert error={updateBodyState.error} />
      )}
      {updateAlwaysDisplayState.error && (
        <RTKQueryErrorAlert error={updateAlwaysDisplayState.error} />
      )}
    </div>
  );
}

function TextBlockCard({
  textBlock,
  isEditing,
  editTitle,
  editBody,
  editAlwaysDisplay,
  onStartEdit,
  onCancelEdit,
  onChangeTitle,
  onChangeBody,
  onChangeAlwaysDisplay,
  onSave,
  isSaving,
  onToggleAlwaysDisplay,
  isAlwaysDisplayUpdating,
}: {
  textBlock: TextBlockResponse;
  isEditing: boolean;
  editTitle: string;
  editBody: string;
  editAlwaysDisplay: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeTitle: (title: string) => void;
  onChangeBody: (body: string) => void;
  onChangeAlwaysDisplay: (val: boolean) => void;
  onSave: () => void;
  isSaving: boolean;
  onToggleAlwaysDisplay: (next: boolean) => Promise<void>;
  isAlwaysDisplayUpdating: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: textBlock.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const handleToggleAlwaysDisplay = useCallback(
    (next: boolean) => {
      if (isEditing) {
        onChangeAlwaysDisplay(next);
        return;
      }
      onToggleAlwaysDisplay(next).catch(() => {
        // Error toast and inline alert are handled in SectionTextBlocks.
      });
    },
    [isEditing, onChangeAlwaysDisplay, onToggleAlwaysDisplay],
  );

  return (
    <div ref={setNodeRef} style={style} className="rounded-md border">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            type="button"
            className="hover:bg-muted cursor-grab rounded p-1"
            title={t("Verschieben")}
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </button>
          <div className="text-sm font-medium">
            {textBlock.title || t("(Ohne Titel)")}
          </div>
          <div className="text-muted-foreground text-sm">
            {t("Reihenfolge")}: {textBlock.order}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="outline" onClick={onStartEdit}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("Bearbeiten")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="p-3">
        <div className="mb-3 flex items-center gap-2">
          <Checkbox
            checked={isEditing ? editAlwaysDisplay : textBlock.alwaysDisplay}
            disabled={isAlwaysDisplayUpdating}
            onCheckedChange={(v) => {
              const next = !!v;
              handleToggleAlwaysDisplay(next);
            }}
          />
          <div className="text-sm">{t("Standardmäßig anzeigen")}</div>
        </div>

        {isEditing ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium">{t("Titel")}</div>
              <Input
                value={editTitle}
                onChange={(e) => onChangeTitle(e.target.value)}
                placeholder={t("Titel")}
              />
            </div>
            <TipTapHtmlEditor
              value={editBody}
              onChange={onChangeBody}
              disabled={isSaving}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={onSave} disabled={isSaving}>
                {t("Speichern")}
              </Button>
              <Button size="sm" variant="outline" onClick={onCancelEdit}>
                {t("Abbrechen")}
              </Button>
            </div>
          </div>
        ) : (
          <TipTapHtmlEditor
            value={textBlock.body || ""}
            editable={false}
            showToolbar={false}
          />
        )}
      </div>
    </div>
  );
}

function TipTapHtmlEditor({
  value,
  onChange,
  disabled,
  editable = true,
  showToolbar = editable,
}: {
  value: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  editable?: boolean;
  showToolbar?: boolean;
}) {
  const isEditable = editable && !disabled;

  const editor = useEditor(
    {
      content: value,
      editable: isEditable,
      onUpdate: ({ editor: instance }) => {
        if (isEditable && onChange) {
          onChange(editorGenerateHTML(instance));
        }
      },
    },
    [isEditable],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }

    const current = editorGenerateHTML(editor);
    const target = value || "";
    if (current !== target) {
      editor.commands.setContent(target);
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  return (
    <Editor editor={editor}>
      {showToolbar ? (
        <EditorToolbar>
          <EditorBoldItalicUnderlineGroup />
          <EditorToolbarSeparator />
          <EditorBulletList />
          <EditorOrderedList />
        </EditorToolbar>
      ) : null}
      <EditorContent />
    </Editor>
  );
}
